import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  TimePicker,
  Row,
  Col,
  message,
  Spin,
  Modal
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { Dayjs } from 'dayjs';
import { AuthContext } from "../../Auth/AuthContext";
import { useNavigate, useLocation } from 'react-router-dom';
import { updateOvertime, getOvertimeById, getallTask } from '../../utils/API'; // Add getallTask import
import { useContext } from 'react';
import { useParams } from 'react-router-dom';

const { Option } = Select;

interface OvertimeData {
  description: string;
  title: string;
  status: string;
  date: string;
  startTime: string;
  endTime: string;
  remarks: string;
  hours: string;
  username?: string;
  userId?: {
    username: string;
  }
}

interface Task {
  _id: string;
  taskName: string;
  taskDesc: string;
  // Add other task fields as needed
}
interface EditovertimeModalProps {
  visible: boolean;
  taskId?: string; // Changed from customerId to taskId
  onClose?: () => void;
  onEdit?: () => void;
  onCancel?: () => void;
  onSuccess?: () => void;
}
const Editovertimemodal: React.FC<EditovertimeModalProps> = (props) => {
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = React.useState(false);
  const navigate = useNavigate();
  const { visible, onClose, taskId, onSuccess } = props;
  const authData = useContext(AuthContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const [totalHours, setTotalHours] = useState<string>('0.00');
  const [formattedHours, setFormattedHours] = useState<string>('0h 00m');
  const { id } = useParams();
  const [taskLoading, setTaskLoading] = useState<boolean>(false);
  const [tasks, setTasks] = useState<Task[]>([]);

  // Fetch all tasks for the dropdown
  useEffect(() => {
    const fetchTasks = async () => {
      try {
        setTaskLoading(true);
        const response = await getallTask('', 1); // Adjust parameters as needed
        if (response && response.data) {
          setTasks(response.data);
        }
      } catch (error) {
        console.error('Error fetching tasks:', error);
        message.error('Failed to load tasks');
      } finally {
        setTaskLoading(false);
      }
    };

    fetchTasks();
  }, []);

  // Professional time calculation function
  const calculateHours = (start: Moment | null, end: Moment | null) => {
    if (!start || !end) {
      setTotalHours('0.00');
      setFormattedHours('0h 00m');
      return;
    }

    if (end.isBefore(start)) {
      message.error('End time must be after start time');
      setTotalHours('0.00');
      setFormattedHours('0h 00m');
      return;
    }

    const duration = moment.duration(end.diff(start));
    const totalHours = duration.asHours();
    const hours = Math.floor(totalHours);
    const minutes = Math.round((totalHours - hours) * 60);

    // Handle cases where minutes round to 60
    const adjustedHours = minutes === 60 ? hours + 1 : hours;
    const adjustedMinutes = minutes === 60 ? 0 : minutes;

    setTotalHours(totalHours.toFixed(2));
    setFormattedHours(`${adjustedHours}h ${adjustedMinutes.toString().padStart(2, '0')}m`);
  };

  // Handle time changes
  const handleTimeChange = () => {
    const startTime = form.getFieldValue('startTime');
    const endTime = form.getFieldValue('endTime');
    calculateHours(startTime, endTime);
  };

  // Handle task selection
  const handleTaskSelect = (taskId: string) => {
    const selectedTask = tasks.find(task => task._id === taskId);
    if (selectedTask) {
      form.setFieldsValue({
        title: selectedTask.taskName,
        description: selectedTask.taskDesc,
      });
    }
  };

  // Load overtime data
  useEffect(() => {
    if (taskId) { // Changed from id to taskId
      const fetchOvertimeData = async () => {
        try {
          setDataLoading(true);
          const response = await getOvertimeById(taskId); // Changed from id to taskId
          const data: OvertimeData = response.data;

          const initialValues = {
            userId: data.userId,
            title: data.title,
            description: data.description,
            name: data.username || data.userId?.username || authData?.username || 'Unknown',
            status: data.status,
            startDate: moment(data.date, 'DD-MM-YYYY'),
            startTime: moment(data.startTime, 'HH:mm'),
            endTime: moment(data.endTime, 'HH:mm'),
            remarks: data.remarks || '',
          };

          form.setFieldsValue(initialValues);
          calculateHours(initialValues.startTime, initialValues.endTime);
        } catch (error) {
          console.error('Error fetching overtime data:', error);
          message.error('Failed to load overtime data');
        } finally {
          setDataLoading(false);
        }
      };

      fetchOvertimeData();
    } else {
      form.setFieldsValue({
        name: authData?.username || 'Unknown',
        status: 'Pending',
      });
      setDataLoading(false);
    }
  }, [taskId, form, authData]); // Changed from id to taskId in dependencies


  const onFinish = async (values: any) => {
    try {
      setLoading(true);

      // Get existing remarks from form or set default
      const remarks = form.getFieldValue('remarks') || "Updated via system";

      const formattedData = {
        userId: values.userId,
        title: values.title,
        description: values.description,
        status: values.status,
        date: values.startDate.format('DD-MM-YYYY'),
        startTime: values.startTime.format('HH:mm'),
        endTime: values.endTime.format('HH:mm'),
        hours: totalHours,
        remarks: remarks,
      };

      if (taskId) {  // Changed from id to taskId
        await updateOvertime(taskId, formattedData);  // Changed from id to taskId
        message.success('Overtime updated successfully!');

        // Call onSuccess callback if provided
        if (onSuccess) {
          onSuccess();
        }

        // Close the modal
        if (onClose) {
          onClose();
        }
      } else {
        message.error('Missing task ID — cannot update.');
      }
    } catch (error) {
      console.error('Error updating overtime:', error);
      message.error('Failed to update overtime. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const disabledStartTime = (date: Dayjs) => {
    const endTime = form.getFieldValue('endTime');
    if (!endTime) return { disabledHours: () => [], disabledMinutes: () => [] };

    const endHour = endTime.hour();
    const endMinute = endTime.minute();

    return {
      disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter(hour => hour > endHour),
      disabledMinutes: () =>
        date.hour() === endHour
          ? Array.from({ length: 60 }, (_, i) => i).filter(minute => minute >= endMinute)
          : [],
    };
  };

  const disabledEndTime = (date: Dayjs) => {
    const startTime = form.getFieldValue('startTime');
    if (!startTime) return { disabledHours: () => [], disabledMinutes: () => [] };

    const startHour = startTime.hour();
    const startMinute = startTime.minute();

    return {
      disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter(hour => hour < startHour),
      disabledMinutes: () => (date.hour() === startHour
        ? Array.from({ length: 60 }, (_, i) => i).filter(minute => minute <= startMinute)
        : []),
    };
  };

  if (dataLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  const commonInputStyle = {
    width: '70%',
    height: 40,
    borderRadius: 6,
    fontSize: 14,
  };


  return (
    <Modal

      visible={visible}
      onCancel={onClose}
      footer={null}
      width={700}
      centered={false} // Disable centered to use top positioning
      destroyOnClose
      className="custom-modal"
      style={{
        bottom: 20,
        top: 20,
        maxHeight: 'calc(100vh - 3  0px)', // Leaves 20px space at bottom
        margin: '0 auto',
        position: 'relative',
        // paddingBottom: 20 // Adds space inside modal at bottom
      }}
      bodyStyle={{
        overflowY: "auto",
        fontFamily: "inter, sans-serif",
      }}
      styles={{
        header: {
          padding: "12px",
          position: "sticky",
          top: 0,
          backgroundColor: "#f1f5ffff",
        },
        content: {
          padding: 0,
          overflow: "hidden",
        },
      }}
      title={
        <div
          style={{
            color: "#4b5563",
            fontFamily: "inter, sans-serif",
            fontSize: "20px",
            fontWeight: 500,
          }}
        >
          Edit Overtime
        </div>
      }
    >
      <Form
        onFinish={onFinish}
        form={form}
        layout="horizontal"
        labelCol={{ span: 5 }}
        wrapperCol={{ span: 19 }}
        colon={false}
      >
        <div className="elements">
          <Form.Item
            name="title"
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Title <span className="text-danger">*</span></span>}
            rules={[{ required: false, message: 'Please enter a task' }]}

          >
            <Select
              className='CustomSelect'
              showSearch
              placeholder="Select a task"
              optionFilterProp="children"
              loading={taskLoading}
              onChange={handleTaskSelect}
              filterOption={(input, option) =>
                (typeof option?.children === 'string' ? (option.children as string).toLowerCase() : '').includes(input.toLowerCase())
              }
            >
              {tasks.map(task => (
                <Option key={task._id} value={task._id}>
                  {task.taskName}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Task Description <span className="text-danger">*</span></span>}
            rules={[{ required: true, message: 'Please enter a description' }]}
            required={false}
          >
            <Input.TextArea rows={3} className="Allinputtextarea" placeholder="Description" />
          </Form.Item>

          <Form.Item
            name="startDate"
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Date <span className="text-danger">*</span></span>}
            rules={[{ required: true, message: 'Please select start date' }]}
            required={false}
          >
            <DatePicker format="DD-MM-YYYY" className='Allinput' />
          </Form.Item>

          <Form.Item
            name="startTime"
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Start Time <span className="text-danger">*</span></span>}
            rules={[{ required: true, message: 'Please select start Time' }]}
            required={false}
          >
            <TimePicker
              use12Hours
              format="h:mm a"
              disabledTime={disabledStartTime}
              onChange={handleTimeChange}
              className='Allinput'
            />
          </Form.Item>

          <Form.Item name="remarks" hidden>
            <Input type="hidden" />
          </Form.Item>

          <Form.Item
            name="endTime"
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>End Time <span className="text-danger">*</span></span>}
            rules={[{ required: true, message: 'Please select end time' }]}
            required={false}
          >
            <TimePicker
              use12Hours
              format="h:mm a"
              disabledTime={disabledEndTime}
              onChange={handleTimeChange}
              className='Allinput'
            />
          </Form.Item>

          <Form.Item
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Total Hours</span>}
            required={false}
          >
            <Input
              // className="rounded border border-1"
              value={` ${formattedHours}`}
              disabled
              className='Allinput'
            />
          </Form.Item>

          <Form.Item
            name="status"
            label={<span style={{
              color: '#4b5563',
              fontFamily: 'inter, sans-serif',
              fontSize: '14px',
              paddingLeft: "15px",
              fontWeight: 500,
            }}>Status</span>}
            rules={[{ required: true, message: 'Please select status' }]}
            required={false}
          >
            <Select placeholder="Select status" className='CustomSelect' disabled={true}>
              <Option value="Pending">Pending</Option>
              <Option value="In Progress">Approved</Option>
              <Option value="Completed">Rejected</Option>
            </Select>
          </Form.Item>



        </div>

        <Row justify="end">
          <Col
            xs={24}
            sm={24}
            md={24}
            xl={24}
            xxl={20}
            className="d-flex justify-content-end me-3"
          >
            <div className="button-wrapper">
              <button
                className="allcanclebutton"
                type='button'
                onClick={onClose}

                disabled={isLoading}
              >
                Cancel
              </button>

              <button className="allsubmitebutton"
                type="submit" // Explicit submit button
                disabled={isLoading}>
                {isLoading ? "Updating..." : "Update"}
              </button>
            </div>
          </Col>
        </Row>



      </Form>


    </Modal>

  );
};

export default Editovertimemodal;